"use client";

import { Close } from "@mui/icons-material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import React, { useEffect, useRef, useState } from "react";

import { useMatchBreakpoints } from "../../hooks";
import { Button, ButtonSize } from "../Button";
import { Text, TextSize, TextWeight } from "../Text";

export interface SortOption {
  id: string;
  label: string;
}

type SortingProps = {
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  sortOptions: SortOption[];
  onSort: (option: string) => void;
};

const Sorting: React.FC<SortingProps> = ({
  selectedOption,
  setSelectedOption,
  sortOptions,
  onSort,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isXs, isSm, isMd } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;

  // Hook do obsługi kliknięć poza dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isOpen &&
        !isMobile // Tylko dla desktop dropdown
      ) {
        // Poczekaj na zaktualizowanie selectedOption, a potem wykonaj sortowanie
        setTimeout(() => {
          handleSort();
        }, 0);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isMobile, selectedOption]); // Dodany selectedOption do dependencies

  const handleOptionChange = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleSort = () => {
    setIsOpen(false);
    onSort(selectedOption);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const renderContent = () => {
    return (
      <>
        {sortOptions.map((option) => (
          <label
            key={option.id}
            className={`flex items-center cursor-pointer group px-3 py-3 transition-colors duration-200 ${
              selectedOption === option.id ? "bg-gray-100" : "hover:bg-gray-50"
            }`}
          >
            <div className="relative">
              <input
                type="radio"
                name="sort"
                value={option.id}
                checked={selectedOption === option.id}
                onChange={() => handleOptionChange(option.id)}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedOption === option.id
                    ? "border-black bg-black"
                    : "border-gray-300 bg-white group-hover:border-gray-400"
                }`}
              >
                {selectedOption === option.id && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
            </div>
            <span
              className={`ml-3 text-sm ${
                selectedOption === option.id
                  ? "font-medium text-gray-900"
                  : "text-gray-700 group-hover:text-gray-900"
              }`}
            >
              <Text text={option.label} fontSize={TextSize.S} />
            </span>
          </label>
        ))}

        <div className="px-4 pt-4 pb-2 w-full">
          <Button
            text="Sortuj"
            fullWidth
            className="!w-full"
            size={ButtonSize.S}
            onClick={handleSort}
          />
        </div>
      </>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-fit flex items-center justify-between gap-2 cursor-pointer transition-colors duration-200"
        onClick={toggleDropdown}
      >
        <Text
          text="SORTUJ"
          fontSize={TextSize.XS}
          textTransform="uppercase"
          className="tracking-widest"
          weight={
            selectedOption !== "default" ? TextWeight.BOLD : TextWeight.NORMAL
          }
        />
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Sort Options - collapsible */}
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={isOpen}
          onClose={() => handleSort()}
          onOpen={() => setIsOpen(true)}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <div className="p-4">
            <div
              className="flex justify-end  cursor-pointer"
              onClick={() => setIsOpen(false)}
            >
              <Close />
            </div>

            {renderContent()}
          </div>
        </SwipeableDrawer>
      ) : (
        /* Desktop Dropdown */
        isOpen && (
          <div className="absolute top-8 left-0 border border-gray-200 bg-white min-w-80 py-2 z-10">
            {renderContent()}
          </div>
        )
      )}
    </div>
  );
};

export default Sorting;
